import { useState, useRef, useEffect, useCallback } from "react";

export const hasClosest = (target, selector) =>
	Boolean(
		target && typeof target.closest === "function" && target.closest(selector)
	);

/**
 * Hook con el estado e interacciones compartidas de las tablas de necesidades
 * (selección de día, hover de celda + tooltip diferido y hover del header).
 *
 * Se extrae de PlanStaffingLevels para reutilizarlo tal cual en la tabla de
 * necesidades fraccionadas, manteniendo exactamente el mismo comportamiento.
 */
export const useStaffingTableInteractions = () => {
	const [selectedDay, setSelectedDay] = useState(null);
	const [hoveredCellId, setHoveredCellId] = useState(null);
	const [tooltipCellId, setTooltipCellId] = useState(null);
	const [hoveredHeaderDay, setHoveredHeaderDay] = useState(null);

	const hoverTimeoutRef = useRef(null);
	const tooltipTimeoutRef = useRef(null);
	const headerHoverTimeoutRef = useRef(null);

	const handleDayClick = useCallback((day) => {
		setSelectedDay((current) => (day === current ? null : day));
	}, []);

	const handleHeaderMouseEnter = useCallback((day) => {
		if (headerHoverTimeoutRef.current) {
			clearTimeout(headerHoverTimeoutRef.current);
			headerHoverTimeoutRef.current = null;
		}
		setHoveredHeaderDay(day);
	}, []);

	const handleHeaderMouseLeave = useCallback((e) => {
		const relatedTarget = e.relatedTarget;
		if (
			relatedTarget &&
			(hasClosest(relatedTarget, ".staffing-header-hover-menu") ||
				hasClosest(relatedTarget, ".btn-hover-action"))
		) {
			return;
		}

		headerHoverTimeoutRef.current = setTimeout(() => {
			setHoveredHeaderDay(null);
		}, 200);
	}, []);

	const handleCellMouseEnter = useCallback((cellKey) => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
		if (tooltipTimeoutRef.current) {
			clearTimeout(tooltipTimeoutRef.current);
			tooltipTimeoutRef.current = null;
		}

		setTooltipCellId(null);
		setHoveredCellId(cellKey);

		tooltipTimeoutRef.current = setTimeout(() => {
			setTooltipCellId(cellKey);
		}, 500);
	}, []);

	const handleCellMouseLeave = useCallback(
		(e, cellKey) => {
			const relatedTarget = e?.relatedTarget;

			if (relatedTarget instanceof Node) {
				if (hasClosest(relatedTarget, ".shift-hover-menu-container")) {
					return;
				}

				const cell =
					e.currentTarget?.closest?.(".need-cell") || e.currentTarget;
				if (cell instanceof Node && cell.contains(relatedTarget)) {
					return;
				}

				if (relatedTarget instanceof Element) {
					const nextCell = relatedTarget.closest(".need-cell");
					if (nextCell && nextCell !== cell) {
						const nextCellKey = nextCell.dataset.cellKey;
						if (nextCellKey) {
							handleCellMouseEnter(nextCellKey);
						}
						return;
					}
				}
			}

			if (tooltipTimeoutRef.current) {
				clearTimeout(tooltipTimeoutRef.current);
				tooltipTimeoutRef.current = null;
			}
			setTooltipCellId((current) => (current === cellKey ? null : current));

			hoverTimeoutRef.current = setTimeout(() => {
				setHoveredCellId((current) => (current === cellKey ? null : current));
			}, 100);
		},
		[handleCellMouseEnter]
	);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
			if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
			if (headerHoverTimeoutRef.current)
				clearTimeout(headerHoverTimeoutRef.current);
		};
	}, []);

	return {
		selectedDay,
		hoveredCellId,
		tooltipCellId,
		hoveredHeaderDay,
		handleDayClick,
		handleHeaderMouseEnter,
		handleHeaderMouseLeave,
		handleCellMouseEnter,
		handleCellMouseLeave,
	};
};
